/**
 * Homepage JS
 */
(function() {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHtml = value => String(value || '').replace(/[&<>"']/g, ch => ESC[ch]);
  const afterElementMotion = (element, callback) => {
    const finish = () => callback?.();
    window.requestAnimationFrame(() => {
      const animations = element?.getAnimations?.().filter(animation => animation.playState !== 'finished');
      if (!animations?.length) {
        finish();
        return;
      }
      Promise.allSettled(animations.map(animation => animation.finished)).then(finish);
    });
  };
  const safeHttpUrl = value => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      return url;
    } catch (e) {
      return '';
    }
  };
  const safeHref = value => {
    const url = safeHttpUrl(value);
    if (!url) return '#';
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  };
  const sizedImageUrl = (value, width) => {
    const url = safeHttpUrl(value);
    if (!url) return '';
    url.searchParams.delete('quality');
    url.searchParams.set('width', String(width));
    return url.href;
  };
  // Shared axis-locked swipe helper - prevents accidental horizontal swipes during vertical scroll
  const bindAxisLockedSwipe = (el, { shouldHandle, onStart, onSwipe, onDrag, onEnd }) => {
    let startX = 0;
    let startY = 0;
    let locked = null;
    let dragOffset = 0;
    let active = false;
    const LOCK = 10;
    const SWIPE = 50;

    el.addEventListener('touchstart', e => {
      active = shouldHandle ? shouldHandle() : true;
      if (!active) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      locked = null;
      dragOffset = 0;
      onStart?.();
    }, { passive: true });

    el.addEventListener('touchmove', e => {
      if (!active) return;
      if (locked === 'y') return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!locked && (Math.abs(dx) > LOCK || Math.abs(dy) > LOCK)) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (locked !== 'x') return;
      e.preventDefault();
      dragOffset = dx;
      onDrag?.(dragOffset);
    }, { passive: false });

    el.addEventListener('touchend', () => {
      if (!active) return;
      if (locked !== 'x') {
        active = false;
        return;
      }
      if (Math.abs(dragOffset) > SWIPE) {
        onSwipe?.(dragOffset > 0 ? -1 : 1);
      }
      onEnd?.(dragOffset);
      active = false;
    }, { passive: true });

    el.addEventListener('touchcancel', () => {
      active = false;
    }, { passive: true });
  };

  // Generic carousel base class
  class Carousel {
    constructor(el, options = {}) {
      this.el = el;
      this.viewport = options.vp || $('[data-carousel-viewport]', el);
      this.track = options.tr || $('[data-carousel-track]', el);
      this.items = $$('[data-carousel-item]', el);
      this.prev = $('[data-carousel-prev]', el);
      this.next = $('[data-carousel-next]', el);
      this.pagination = options.pg || $('[data-carousel-pagination]', el);
      this.dotClass = options.dot || 'dot';
      this.draggingClass = options.drg || 'dragging';
      this.currentIndex = 0;
      this.perSlide = 1;
      this.suppressClick = false;
    }

    get total() {
      return Math.ceil(this.items.length / this.perSlide);
    }

    getItemWidth() {
      return this.items[0]?.offsetWidth + 16 || 0;
    }

    makePagination() {
      if (!this.pagination) return;
      if (this.total <= 1) {
        this.pagination.innerHTML = '';
        return;
      }
      this.pagination.innerHTML = Array.from({ length: this.total }, (_, i) =>
        `<button type="button" class="${this.dotClass}${i === this.currentIndex ? ` ${this.dotClass}--active` : ''}" aria-label="Slide ${i + 1}"${i === this.currentIndex ? ' aria-current="true"' : ''}></button>`
      ).join('');
      if (!this.paginationBound) {
        this.pagination.addEventListener('click', e => {
          const btn = e.target.closest('button');
          if (btn) this.go([...this.pagination.children].indexOf(btn));
        });
        this.paginationBound = true;
      }
    }

    bind() {
      this.prev?.addEventListener('click', () => this.goPrev());
      this.next?.addEventListener('click', () => this.goNext());
      this.el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') this.goPrev();
        else if (e.key === 'ArrowRight') this.goNext();
      });
      this.track?.addEventListener('click', e => {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { capture: true });
    }

    bindDrag() {
      if (!this.viewport || this.total <= 1) return;
      bindAxisLockedSwipe(this.viewport, {
        onSwipe: dir => this.go(this.currentIndex + dir),
        onDrag: offset => {
          this.track.classList.add(this.draggingClass);
          this.track.style.transform = `translate3d(${-this.currentIndex * this.getItemWidth() * this.perSlide + offset}px,0,0)`;
        },
        onEnd: () => {
          this.track.classList.remove(this.draggingClass);
          this.go(this.currentIndex);
          this.suppressClick = true;
          setTimeout(() => this.suppressClick = false, 180);
        }
      });
    }

    goPrev() {
      if (this.currentIndex > 0) this.go(this.currentIndex - 1);
    }

    goNext() {
      if (this.currentIndex < this.total - 1) this.go(this.currentIndex + 1);
    }

    go(i) {
      this.currentIndex = Math.max(0, Math.min(i, this.total - 1));
      this.track.style.transform = `translate3d(${-this.currentIndex * this.getItemWidth() * this.perSlide}px,0,0)`;
      this.updateUI();
    }

    updateUI() {
      if (this.prev) this.prev.disabled = this.currentIndex === 0;
      if (this.next) this.next.disabled = this.currentIndex === this.total - 1;
      if (this.pagination) {
        $$('.' + this.dotClass, this.pagination).forEach((dot, i) => {
          const active = i === this.currentIndex;
          dot.classList.toggle(this.dotClass + '--active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
    }

  }

  // Shared snap carousel with Flickity-like grouped cells and pointer physics.
  class NativeSnapCarousel extends Carousel {
    constructor(el, options = {}) {
      super(el, {
        dot: options.dot || 'native-carousel__dot',
        drg: options.drg || 'native-carousel__track--dragging'
      });
      this.wrapperDraggingClass = options.wrapperDraggingClass || 'native-carousel-wrapper--dragging';
      this.selectedItemClass = options.selectedItemClass || 'native-carousel__item--selected';
      this.snapItemClass = options.snapItemClass || 'native-carousel__item--snap';
      this.instanceProp = options.instanceProp || '';
      this.hideInactiveItems = options.hideInactiveItems !== false;
      this.nativeSnapAlign = options.nativeSnapAlign || 'start';
      this.fixedGroupSize = options.fixedGroupSize || 0;
      this.fixedGroupSizeBreakpoint = options.fixedGroupSizeBreakpoint || 0;
      this.centerTrailingGroup = !!options.centerTrailingGroup;
      this.ready = false;
      if (!this.items.length || !this.track || !this.viewport) return;

      this.abortController = new AbortController();
      this.groups = [];
      this.snaps = [0];
      this.currentIndex = 0;
      this.x = 0;
      this.targetX = 0;
      this.velocityX = 0;
      this.pointerVelocity = 0;
      this.dragState = null;
      this.raf = 0;
      this.nativeScrollRaf = 0;
      this.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      this.cache();
      this.makePagination();
      this.bind();
      this.select(0, { immediate: true });
      this.ready = true;
    }

    get total() {
      return this.groups.length || 1;
    }

    isNativeScroll() {
      return innerWidth < 768;
    }

    activeFixedGroupSize() {
      const groupSize = Number.parseInt(this.fixedGroupSize, 10) || 0;
      if (!groupSize) return 0;
      if (this.fixedGroupSizeBreakpoint && innerWidth < this.fixedGroupSizeBreakpoint) return 0;
      return groupSize;
    }

    cache() {
      const allItems = $$('[data-carousel-item]', this.el);
      allItems.forEach(item => item.classList.remove(this.snapItemClass));
      this.items = allItems.filter(item => getComputedStyle(item).display !== 'none');
      if (!this.items.length) {
        this.groups = [];
        this.snaps = [0];
        this.minX = 0;
        this.maxX = 0;
        return;
      }

      const trackStyles = getComputedStyle(this.track);
      const first = this.items[0];
      const firstLeft = first.offsetLeft;
      const rightPad = parseFloat(trackStyles.paddingRight) || 0;
      const viewportWidth = this.viewport.clientWidth;
      const viewSpan = Math.max(1, viewportWidth - firstLeft - rightPad);
      const positions = this.items.map(item => item.offsetLeft - firstLeft);
      const widths = this.items.map(item => item.getBoundingClientRect().width);
      const lastIndex = this.items.length - 1;
      const contentWidth = positions[lastIndex] + widths[lastIndex];
      const maxOffset = Math.max(0, contentWidth - viewSpan);
      const groups = [];
      const fixedGroupSize = this.activeFixedGroupSize();

      if (fixedGroupSize) {
        for (let start = 0; start < this.items.length; start += fixedGroupSize) {
          const end = Math.min(start + fixedGroupSize - 1, lastIndex);
          const count = end - start + 1;
          let offset = positions[start];

          if (this.centerTrailingGroup && count < fixedGroupSize) {
            const groupWidth = positions[end] + widths[end] - positions[start];
            offset = positions[start] - Math.max(0, viewSpan - groupWidth) / 2;
          }

          groups.push({ start, end, offset: Math.max(0, offset) });
        }

        const maxGroupOffset = groups.reduce((max, group) => Math.max(max, group.offset), maxOffset);
        this.groups = groups.length ? groups : [{ start: 0, end: lastIndex, offset: 0 }];
        this.snaps = this.groups.map(group => -group.offset);
        this.minX = -maxGroupOffset;
        this.maxX = 0;
        this.groups.forEach(group => this.items[group.start]?.classList.add(this.snapItemClass));
        return;
      }

      for (let i = 0; i < this.items.length;) {
        const start = i;
        const startPosition = positions[start];
        let end = start;
        while (
          end + 1 < this.items.length
          && positions[end + 1] + widths[end + 1] - startPosition <= viewSpan + 1
        ) {
          end += 1;
        }

        const offset = Math.min(startPosition, maxOffset);
        const previous = groups[groups.length - 1];
        if (!previous || Math.abs(previous.offset - offset) > 1) {
          groups.push({ start, end, offset });
        } else {
          previous.end = end;
        }
        i = end + 1;
      }

      this.groups = groups.length ? groups : [{ start: 0, end: lastIndex, offset: 0 }];
      this.snaps = this.groups.map(group => -group.offset);
      this.minX = -maxOffset;
      this.maxX = 0;
      this.groups.forEach(group => this.items[group.start]?.classList.add(this.snapItemClass));
    }

    makePagination() {
      if (!this.pagination) return;
      if (this.total <= 1) {
        this.pagination.innerHTML = '';
        return;
      }

      this.pagination.innerHTML = Array.from({ length: this.total }, (_, i) =>
        `<button type="button" class="${this.dotClass}" aria-label="Ga naar slide ${i + 1} van ${this.total}"></button>`
      ).join('');
    }

    bind() {
      const signal = this.abortController.signal;
      const add = (target, type, handler, options = {}) => {
        target?.addEventListener(type, handler, { ...options, signal });
      };

      add(this.prev, 'click', () => this.select(this.currentIndex - 1));
      add(this.next, 'click', () => this.select(this.currentIndex + 1));
      add(this.pagination, 'click', e => {
        const btn = e.target.closest('button');
        if (!btn) return;
        this.select([...this.pagination.children].indexOf(btn));
      });
      add(this.el, 'keydown', e => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.select(this.currentIndex - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.select(this.currentIndex + 1);
        }
      });
      add(this.track, 'click', e => {
        if (!this.suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true });
      add(this.track, 'dragstart', e => e.preventDefault());

      if (window.PointerEvent) {
        add(this.viewport, 'pointerdown', e => this.onPointerDown(e));
        add(this.viewport, 'pointermove', e => this.onPointerMove(e));
        add(this.viewport, 'pointerup', e => this.onPointerUp(e));
        add(this.viewport, 'pointercancel', e => this.onPointerUp(e, true));
      } else {
        this.bindTouchFallback();
      }

      add(this.viewport, 'scroll', () => this.onNativeScroll(), { passive: true });

      let resizeTimer;
      add(window, 'resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.refresh(), 120);
      }, { passive: true });
    }

    bindTouchFallback() {
      let startIndex = this.currentIndex;
      let swipeDirection = 0;

      bindAxisLockedSwipe(this.viewport, {
        shouldHandle: () => !this.isNativeScroll(),
        onStart: () => {
          startIndex = this.currentIndex;
          swipeDirection = 0;
        },
        onSwipe: dir => {
          swipeDirection = dir;
        },
        onDrag: offset => {
          this.track.classList.add(this.draggingClass);
          this.setX(this.withResistance(this.targetX + offset));
        },
        onEnd: () => {
          let nextIndex = this.nearestIndex(this.x);
          if (swipeDirection && nextIndex === startIndex) {
            nextIndex = startIndex + swipeDirection;
          }

          this.track.classList.remove(this.draggingClass);
          this.select(this.limitDragTarget(nextIndex, startIndex));
          this.suppressClickOnce();
        }
      });
    }

    refresh() {
      const previousIndex = this.currentIndex;
      this.stopAnimation();
      if (this.isNativeScroll()) {
        this.track.style.transform = '';
      } else if (this.viewport.scrollLeft) {
        this.viewport.scrollLeft = 0;
      }
      this.cache();
      this.makePagination();
      this.select(Math.min(previousIndex, this.total - 1), { immediate: true });
    }

    onPointerDown(e) {
      if (this.isNativeScroll()) return;
      if (this.total <= 1 || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (e.target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-pagination]')) return;

      this.stopAnimation();
      this.pointerVelocity = 0;
      this.dragState = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startTranslate: this.x,
        lastX: e.clientX,
        lastTime: performance.now(),
        locked: null,
        moved: false,
        startIndex: this.currentIndex
      };
    }

    onPointerMove(e) {
      if (this.isNativeScroll()) return;
      const state = this.dragState;
      if (!state || e.pointerId !== state.id) return;

      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (!state.locked && (Math.abs(dx) > 3 || Math.abs(dy) > 6)) {
        state.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (state.locked === 'y') return;
      if (state.locked !== 'x') return;

      e.preventDefault();
      if (!state.moved) {
        state.moved = true;
        this.viewport.setPointerCapture?.(e.pointerId);
        this.track.classList.add(this.draggingClass);
        this.el.classList.add(this.wrapperDraggingClass);
      }

      const now = performance.now();
      const dt = now - state.lastTime;
      if (dt > 0) {
        const instantVelocity = (e.clientX - state.lastX) / dt;
        this.pointerVelocity = this.pointerVelocity * 0.65 + instantVelocity * 0.35;
      }
      state.lastX = e.clientX;
      state.lastTime = now;

      this.setX(this.withResistance(state.startTranslate + dx));
    }

    onPointerUp(e, cancelled = false) {
      const state = this.dragState;
      if (!state || e.pointerId !== state.id) return;

      if (this.viewport.hasPointerCapture?.(e.pointerId)) {
        this.viewport.releasePointerCapture(e.pointerId);
      }
      this.track.classList.remove(this.draggingClass);
      this.el.classList.remove(this.wrapperDraggingClass);
      this.dragState = null;

      if (!state.moved || state.locked !== 'x') return;

      const dx = e.clientX - state.startX;
      const projected = cancelled ? this.x : this.x + this.pointerVelocity * 260;
      let nextIndex = this.nearestIndex(projected);
      const fast = Math.abs(this.pointerVelocity) > 0.45;
      if (fast && nextIndex === state.startIndex) {
        nextIndex = state.startIndex + (this.pointerVelocity < 0 ? 1 : -1);
      } else if (!fast && Math.abs(dx) > this.viewport.clientWidth * 0.18 && nextIndex === state.startIndex) {
        nextIndex = state.startIndex + (dx < 0 ? 1 : -1);
      }

      this.suppressClickOnce();
      this.select(this.limitDragTarget(nextIndex, state.startIndex), {
        velocity: this.releaseVelocity(this.pointerVelocity)
      });
    }

    nativeOffset(index) {
      const group = this.groups[index];
      if (!group) return 0;
      let offset = group.offset;

      if (this.isNativeScroll() && this.nativeSnapAlign === 'center') {
        const item = this.items[group.start];
        if (item) {
          const itemRect = item.getBoundingClientRect();
          const viewportRect = this.viewport.getBoundingClientRect();
          offset = this.viewport.scrollLeft
            + itemRect.left
            + itemRect.width / 2
            - viewportRect.left
            - viewportRect.width / 2;
        }
      }

      return this.clampNativeOffset(offset);
    }

    offsetForIndex(index) {
      const group = this.groups[index];
      if (!group) return 0;
      return this.clampNativeOffset(group.offset);
    }

    clampNativeOffset(offset) {
      const maxOffset = this.isNativeScroll()
        ? Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth)
        : Math.max(0, -this.minX);
      return Math.max(0, Math.min(offset, maxOffset));
    }

    nearestNativeIndex(scrollLeft = this.viewport.scrollLeft) {
      let nearest = 0;
      let distance = Infinity;
      this.groups.forEach((group, i) => {
        const nextDistance = Math.abs(this.nativeOffset(i) - scrollLeft);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = i;
        }
      });
      return nearest;
    }

    onNativeScroll() {
      if (!this.isNativeScroll() || this.nativeScrollRaf) return;
      this.nativeScrollRaf = requestAnimationFrame(() => {
        this.nativeScrollRaf = 0;
        const next = this.nearestNativeIndex();
        if (next === this.currentIndex) return;
        this.currentIndex = next;
        this.targetX = this.snaps[next] || 0;
        this.x = this.targetX;
        this.updateUI();
      });
    }

    withResistance(x) {
      if (x > this.maxX) return this.maxX + (x - this.maxX) * 0.35;
      if (x < this.minX) return this.minX + (x - this.minX) * 0.35;
      return x;
    }

    nearestIndex(x) {
      const clamped = Math.max(this.minX, Math.min(this.maxX, x));
      let nearest = 0;
      let distance = Infinity;
      this.snaps.forEach((snap, i) => {
        const nextDistance = Math.abs(snap - clamped);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = i;
        }
      });
      return nearest;
    }

    limitDragTarget(index, startIndex) {
      // Keep a manual drag to one adjacent snap group. The velocity projection
      // can otherwise choose a snap several groups away after a hard swipe.
      const min = Math.max(0, startIndex - 1);
      const max = Math.min(this.total - 1, startIndex + 1);
      return Math.max(min, Math.min(index, max));
    }

    releaseVelocity(pointerVelocity) {
      const velocity = pointerVelocity * 16;
      if (!Number.isFinite(velocity)) return 0;
      return Math.max(-28, Math.min(28, velocity));
    }

    select(index, options = {}) {
      const next = Math.max(0, Math.min(index, this.total - 1));
      this.currentIndex = next;
      this.targetX = this.snaps[next] || 0;
      this.updateUI();

      if (this.isNativeScroll()) {
        this.stopAnimation();
        this.velocityX = 0;
        this.x = this.targetX;
        this.track.style.transform = '';
        const left = this.nativeOffset(next);
        const distance = Math.abs(this.viewport.scrollLeft - left);
        if (distance > 1) {
          this.viewport.scrollTo({
            left,
            behavior: options.immediate || this.reduceMotion ? 'auto' : 'smooth'
          });
        }
        return;
      }

      if (this.viewport.scrollLeft) {
        this.viewport.scrollLeft = 0;
      }

      if (options.immediate || this.reduceMotion) {
        this.stopAnimation();
        this.velocityX = 0;
        this.setX(this.targetX);
        return;
      }

      this.velocityX = Number.isFinite(options.velocity) ? options.velocity : 0;
      this.animate();
    }

    animate() {
      this.stopAnimation();
      const attraction = 0.025;
      const friction = 0.72;
      const tick = () => {
        const delta = this.targetX - this.x;
        this.velocityX = (this.velocityX + delta * attraction) * friction;

        if (Math.abs(delta) < 0.35 && Math.abs(this.velocityX) < 0.35) {
          this.setX(this.targetX);
          this.raf = 0;
          return;
        }

        this.setX(this.x + this.velocityX);
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    stopAnimation() {
      if (!this.raf) return;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    setX(x) {
      this.x = x;
      this.track.style.transform = `translate3d(${x}px,0,0)`;
    }

    updateUI() {
      this.el.dataset.carouselPages = String(this.total);
      if (this.total <= 1) this.el.setAttribute('data-carousel-single', 'true');
      else this.el.removeAttribute('data-carousel-single');

      if (this.prev) this.prev.disabled = this.currentIndex === 0 || this.total <= 1;
      if (this.next) this.next.disabled = this.currentIndex >= this.total - 1 || this.total <= 1;

      if (this.pagination) {
        $$('.' + this.dotClass, this.pagination).forEach((dot, i) => {
          const active = i === this.currentIndex;
          dot.classList.toggle(this.dotClass + '--active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      const selectedGroup = this.groups[this.currentIndex];
      this.items.forEach((item, i) => {
        const selected = !!selectedGroup && i >= selectedGroup.start && i <= selectedGroup.end;
        item.classList.toggle('is-selected', selected);
        item.classList.toggle(this.selectedItemClass, selected);
        this.setItemHidden(item, this.hideInactiveItems ? !selected : false);
      });

      this.loadLazyGroups(this.currentIndex, 1);
    }

    setItemHidden(item, hidden) {
      if (hidden) item.setAttribute('aria-hidden', 'true');
      else item.removeAttribute('aria-hidden');
      $$('a[href], button, input, select, textarea, [tabindex]', item).forEach(control => {
        if (hidden) {
          if (!control.hasAttribute('data-carousel-tabindex')) {
            control.dataset.carouselTabindex = control.getAttribute('tabindex') ?? '';
          }
          control.setAttribute('tabindex', '-1');
          return;
        }

        if (!control.hasAttribute('data-carousel-tabindex')) return;
        const previous = control.dataset.carouselTabindex;
        if (previous === '') control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', previous);
        delete control.dataset.carouselTabindex;
      });
    }

    suppressClickOnce() {
      this.suppressClick = true;
      clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = setTimeout(() => {
        this.suppressClick = false;
      }, 220);
    }

    lazy() {
      const lazyItems = $$('[data-product-lazy]', this.el);
      if (!lazyItems.length) return;

      const loadItem = el => {
        if (!el.hasAttribute('data-product-lazy')) return;

        const d = el.dataset;
        const productTitle = escapeHtml(d.productTitle);
        const productPrice = escapeHtml(d.productPrice);
        const productUrlRaw = safeHref(d.productUrl);
        const productUrl = escapeHtml(productUrlRaw);
        const productHandle = escapeHtml(d.productHandle);
        let badgeHtml = '';
        if (d.badgeEnabled === 'true') {
          badgeHtml = '<span class="product-badge product-badge--bestseller">'
            + '<svg class="badge__icon" viewBox="0 0 24 24" fill="currentColor">'
            + '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>'
            + '</svg>Bestseller</span>';
        }

        let imageHtml = '';
        if (d.productImage) {
          const imageSrcset = [160, 200, 240, 280, 320, 360, 400, 480, 560, 600]
            .map(width => {
              const url = sizedImageUrl(d.productImage, width);
              return url ? `${escapeHtml(url)} ${width}w` : '';
            })
            .filter(Boolean)
            .join(',');

          const imageSrc = sizedImageUrl(d.productImage, 360);
          if (imageSrc && imageSrcset) {
            imageHtml = `<img src="${escapeHtml(imageSrc)}"`
              + ` srcset="${imageSrcset}"`
              + ` sizes="(min-width:1280px) calc(min(100vw, 1400px) * 0.20 - 26px),(min-width:1024px) calc((100vw - 50px) * 0.20 - 16px),(min-width:768px) calc((100vw - 98px) / 4),calc((100vw - 31.5px) / 2.15)"`
              + ` class="product-card-compact__image"`
              + ` alt="${productTitle}"`
              + ` loading="lazy" decoding="async" fetchpriority="low" width="300" height="450">`;
          }
        }
        if (!imageHtml) {
          imageHtml = '<div class="product-card-compact__image-placeholder" aria-hidden="true"></div>';
        }

        let ratingHtml = '';
        const ratingValue = Number.parseFloat(d.productRating);
        if (Number.isFinite(ratingValue) && ratingValue > 0) {
          const clampedRating = Math.max(0, Math.min(5, ratingValue));
          const rating = clampedRating.toFixed(1);
          const starFill = clampedRating * 20;
          const ratingScore = Math.round(clampedRating);
          const reviewCount = Math.max(0, Number.parseInt(d.productReviewCount || '0', 10) || 0);
          ratingHtml = `<div class="product-rating product-rating--compact" data-rating="${rating}" data-count="${reviewCount}">`
            + `<div class="product-rating__stars" style="--star-fill:${starFill}%;" role="img" aria-label="${rating} van 5 sterren"></div>`
            + `<span class="product-rating__score">${ratingScore}</span>`
            + `<span class="product-rating__count">(${reviewCount})</span>`
            + '</div>';
        }

        let comparePriceHtml = '';
        if (d.productComparePrice) {
          comparePriceHtml = `<span class="product-card-compact__compare-price">${escapeHtml(d.productComparePrice)}</span>`;
        }

        let discountBadgeHtml = '';
        const discountPercentage = Number.parseInt(d.productDiscountPercentage || '0', 10) || 0;
        if (discountPercentage > 0) {
          discountBadgeHtml = `<span class="product-card-compact__badge product-card-compact__badge--discount">-${discountPercentage}%</span>`;
        }

        el.innerHTML = `<div class="product-card-compact" data-product-handle="${productHandle}">`
          + `<div class="product-card-compact__image-wrapper">${badgeHtml}<a href="${productUrl}" class="product-card-compact__image-link" tabindex="-1" aria-label="${productTitle}">${imageHtml}</a></div>`
          + '<div class="product-card-compact__info">'
          + `<div class="product-card-compact__title"><a href="${productUrl}">${productTitle}</a></div>`
          + `<div class="product-card-compact__rating">${ratingHtml}</div>`
          + '<div class="product-card-compact__price-wrapper">'
          + `${comparePriceHtml}<span class="product-card-compact__price">${productPrice}</span>${discountBadgeHtml}`
          + '</div></div></div>';

        el.classList.remove('native-carousel__item--lazy');
        el.removeAttribute('data-product-lazy');
        this.setItemHidden(el, el.getAttribute('aria-hidden') === 'true');
      };

      this.loadLazyItem = loadItem;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              loadItem(entry.target);
              this.observer.unobserve(entry.target);
            }
          });
        }, { root: this.viewport, rootMargin: '160px', threshold: 0.01 });

        lazyItems.forEach(el => this.observer.observe(el));
      }

      this.loadLazyGroups(this.currentIndex, 1);
    }

    loadLazyGroups(index, radius = 0) {
      if (!this.loadLazyItem || !this.groups.length) return;
      const start = Math.max(0, index - radius);
      const end = Math.min(this.groups.length - 1, index + radius);
      for (let groupIndex = start; groupIndex <= end; groupIndex += 1) {
        const group = this.groups[groupIndex];
        for (let itemIndex = group.start; itemIndex <= group.end; itemIndex += 1) {
          this.loadLazyItem(this.items[itemIndex]);
        }
      }
    }

    destroy() {
      this.abortController?.abort();
      this.stopAnimation();
      if (this.nativeScrollRaf) cancelAnimationFrame(this.nativeScrollRaf);
      this.observer?.disconnect();
      clearTimeout(this.suppressClickTimer);
      this.track?.classList.remove(this.draggingClass);
      this.el.classList.remove(this.wrapperDraggingClass);
      if (this.track) this.track.style.transform = '';
      this.items.forEach(item => {
        item.classList.remove('is-selected', this.selectedItemClass, this.snapItemClass);
        this.setItemHidden(item, false);
      });
      delete this.el.dataset.init;
      delete this.el.dataset.carouselPages;
      this.el.removeAttribute('data-carousel-single');
      if (this.instanceProp) delete this.el[this.instanceProp];
    }

  }

  // Product card carousel keeps the product-specific lazy card loading.
  class ProductCarousel extends NativeSnapCarousel {
    constructor(el) {
      super(el, { instanceProp: '_productCarousel' });
      if (this.ready) this.lazy();
    }
  }

  // Shop-by-style carousel reuses the product carousel controls and snap behavior.
  class StyleCarousel extends NativeSnapCarousel {
    constructor(el) {
      super(el, {
        dot: 'native-carousel__dot',
        drg: 'shop-by-style__track--dragging',
        wrapperDraggingClass: 'shop-by-style__carousel-wrapper--dragging',
        selectedItemClass: 'shop-by-style__item--selected',
        snapItemClass: 'shop-by-style__item--snap',
        nativeSnapAlign: 'start',
        fixedGroupSize: 4,
        fixedGroupSizeBreakpoint: 768,
        centerTrailingGroup: true,
        instanceProp: '_styleCarousel'
      });
    }
  }

  // Blog posts carousel reuses the product carousel controls and snap behavior.
  class BlogCarousel extends NativeSnapCarousel {
    constructor(el) {
      super(el, {
        dot: 'native-carousel__dot',
        drg: 'blog-posts__track--dragging',
        wrapperDraggingClass: 'blog-posts__carousel-wrapper--dragging',
        selectedItemClass: 'blog-posts__item--selected',
        snapItemClass: 'blog-posts__item--snap',
        instanceProp: '_blogCarousel'
      });
    }
  }

  const DEFERRED_MEDIA_SELECTOR = '[data-deferred-media]';
  let deferredMediaObserver = null;

  const getDeferredMedia = root => [
    ...(root.matches?.(DEFERRED_MEDIA_SELECTOR) ? [root] : []),
    ...$$(DEFERRED_MEDIA_SELECTOR, root)
  ];

  const hydrateDeferredMedia = container => {
    if (!container?.matches(DEFERRED_MEDIA_SELECTOR)) return;

    deferredMediaObserver?.unobserve(container);
    $$('[data-deferred-media-fallback]', container).forEach(fallback => fallback.remove());
    $$('template[data-deferred-media-template]', container).forEach(template => {
      const media = document.importNode(template.content, true);
      template.replaceWith(media);
    });
    container.removeAttribute('data-deferred-media');
    delete container.dataset.deferredMediaObserved;
  };

  const initDeferredMedia = root => {
    const media = getDeferredMedia(root);
    if (!media.length) return;

    if (typeof window.IntersectionObserver !== 'function') {
      media.forEach(hydrateDeferredMedia);
      return;
    }

    deferredMediaObserver ||= new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) hydrateDeferredMedia(entry.target);
      });
    }, {
      // Native lazy loading reaches much farther ahead; 600px keeps the next
      // content row warm without pulling deep sections into the initial load.
      rootMargin: '600px 160px',
      threshold: 0
    });

    media.forEach(container => {
      if (container.dataset.deferredMediaObserved) return;
      container.dataset.deferredMediaObserved = '1';
      deferredMediaObserver.observe(container);
    });
  };

  const destroyDeferredMedia = root => {
    getDeferredMedia(root).forEach(container => {
      deferredMediaObserver?.unobserve(container);
      delete container.dataset.deferredMediaObserved;
    });
  };

  const initProductCarousels = root => {
    $$('[data-section-type="product-carousel"]', root).forEach(el => {
      if (el.dataset.init) return;
      el.dataset.init = '1';
      el._productCarousel = new ProductCarousel(el);
    });
  };

  const initStyleCarousel = root => {
    $$('[data-section-type="style-carousel"]', root).forEach(el => {
      if (el.dataset.init) return;
      el.dataset.init = '1';
      el._styleCarousel = new StyleCarousel(el);
    });
  };

  const initBlogCarousels = root => {
    $$('[data-section-type="blog-carousel"]', root).forEach(el => {
      if (el.dataset.init) return;
      el.dataset.init = '1';
      el._blogCarousel = new BlogCarousel(el);
    });
  };

  const initMarquees = root => {
    const supportsVisibilityObserver = typeof window.IntersectionObserver === 'function';

    $$('.testimonials-wrapper', root).forEach(wrapper => {
      if (wrapper.dataset.obs) return;
      const marquees = $$('.marquee-content', wrapper);
      if (!marquees.length) return;
      wrapper.dataset.obs = '1';
      let obs = null;

      const setPlayState = running => {
        marquees.forEach(el => el.style.animationPlayState = running ? 'running' : 'paused');
      };

      wrapper._marquee = {
        destroy() {
          obs?.disconnect();
          marquees.forEach(el => el.style.animationPlayState = '');
          delete wrapper.dataset.obs;
          delete wrapper._marquee;
        }
      };

      setPlayState(false);

      if (!supportsVisibilityObserver) {
        return;
      }

      obs = new window.IntersectionObserver(([entry]) => {
        setPlayState(entry.isIntersecting);
      }, {
        threshold: 0.1,
        rootMargin: '50px'
      });
      obs.observe(wrapper);
    });
  };

  // Review modal with lazy-loaded content
  class Modal {
    constructor() {
      this.modal = $('#reviewModal');
      if (!this.modal) return;
      if (this.modal.dataset.homepageModalReady) return;
      this.modal.dataset.homepageModalReady = '1';
      this.container = $('.review-modal__container', this.modal);
      this.url = this.modal.dataset.reviewsUrl;
      this.isOpen = false;
      this.loaded = false;
      this.loadPromise = null;
      this.lastFocus = null;

      document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-reviews-modal-trigger]');
        if (trigger) {
          e.preventDefault();
          if (!trigger.classList.contains('webwinkelkeurReviews')) this.show(trigger);
        }
      });

      // Close buttons are inside the lazy-loaded container, which stops bubbling.
      // Capture the click at the modal root before that propagation guard runs.
      this.modal.addEventListener('click', e => {
        const btn = e.target.closest('[data-modal-close]');
        if (btn) this.hide();
      }, { capture: true });

      document.addEventListener('keydown', e => {
        this.onKeydown(e);
      });

      this.container?.addEventListener('click', e => e.stopPropagation());
    }

    getFocusable() {
      return $$(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        this.modal
      ).filter(el => {
        if (el.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
        const style = getComputedStyle(el);
        return style.display !== 'none'
          && style.visibility !== 'hidden'
          && el.getClientRects().length > 0;
      });
    }

    focusInitial() {
      requestAnimationFrame(() => {
        const close = $('.review-modal__close', this.modal);
        const target = close || this.getFocusable()[0] || this.container;
        target?.focus?.({ preventScroll: true });
      });
    }

    onKeydown(e) {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = this.getFocusable();
      if (!focusable.length) {
        e.preventDefault();
        this.container?.focus?.({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    load() {
      if (this.loaded || !this.url) return this.loadPromise || Promise.resolve();
      if (this.loadPromise) return this.loadPromise;

      this.loadPromise = (async () => {
        try {
          const res = await fetch(this.url);
          if (!res.ok) throw new Error('Failed to load reviews');
          const html = await res.text();
          const temp = document.createElement('div');
          temp.innerHTML = html;
          const content = temp.querySelector('.shopify-section')?.innerHTML || temp.innerHTML;
          this.container.innerHTML = content;
          this.loaded = true;
          if (this.isOpen) this.focusInitial();
        } catch (e) {
          this.container.innerHTML = '<header class="review-modal__header">'
            + '<button type="button" class="review-modal__close" aria-label="Sluit" data-modal-close>'
            + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">'
            + '<path d="M18 6L6 18M6 6l12 12"/>'
            + '</svg></button>'
            + '<div class="review-modal__header-content">'
            + '<h2 id="reviewModalTitle" class="review-modal__title">Kon reviews niet laden</h2>'
            + '<p class="review-modal__subtitle" role="status">Probeer het later opnieuw.</p>'
            + '</div></header>';
          this.loaded = true;
          if (this.isOpen) this.focusInitial();
        } finally {
          this.loadPromise = null;
        }
      })();

      return this.loadPromise;
    }

    show(trigger) {
      if (this.isOpen) return;
      this.lastFocus = trigger || document.activeElement;
      this.modal.style.display = 'flex';
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.offsetHeight;
      this.modal.classList.add('review-modal--open');
      document.body.classList.add('modal-open');
      this.isOpen = true;
      this.focusInitial();
      if (!this.loaded) this.load();
    }

    hide() {
      if (!this.isOpen) return;
      this.modal.classList.remove('review-modal--open');
      this.modal.classList.add('review-modal--closing');
      afterElementMotion(this.container, () => {
        this.modal.style.display = 'none';
        this.modal.setAttribute('aria-hidden', 'true');
        this.modal.classList.remove('review-modal--closing');
        document.body.classList.remove('modal-open');
        this.isOpen = false;
        if (this.lastFocus?.isConnected) {
          this.lastFocus.focus({ preventScroll: true });
        }
      });
    }

  }

  const initModal = () => $('#reviewModal') && new Modal();

  const ready = fn => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const signalHomepageReady = () => {
    document.dispatchEvent(new CustomEvent('homepage:ready'));
    if (typeof window.__dutchXxlHomepageReady === 'function') {
      window.__dutchXxlHomepageReady();
    }
  };

  ready(() => {
    initDeferredMedia(document);
    initProductCarousels(document);
    initStyleCarousel(document);
    initBlogCarousels(document);
    initMarquees(document);
    initModal();
    signalHomepageReady();
  });

  document.addEventListener('shopify:section:load', e => {
    initDeferredMedia(e.target);
    initProductCarousels(e.target);
    initStyleCarousel(e.target);
    initBlogCarousels(e.target);
    initMarquees(e.target);
  });

  document.addEventListener('shopify:section:unload', e => {
    destroyDeferredMedia(e.target);
    const marquees = [
      ...(e.target.matches?.('.testimonials-wrapper') ? [e.target] : []),
      ...$$('.testimonials-wrapper', e.target)
    ];
    const carousels = [
      ...(e.target.matches?.('[data-section-type="product-carousel"], [data-section-type="style-carousel"], [data-section-type="blog-carousel"]') ? [e.target] : []),
      ...$$('[data-section-type="product-carousel"], [data-section-type="style-carousel"], [data-section-type="blog-carousel"]', e.target)
    ];
    marquees.forEach(el => el._marquee?.destroy?.());
    carousels.forEach(el => {
      el._productCarousel?.destroy();
      el._styleCarousel?.destroy();
      el._blogCarousel?.destroy();
    });
  });

  document.addEventListener('shopify:section:select', e => {
    getDeferredMedia(e.target).forEach(hydrateDeferredMedia);
  });

  document.addEventListener('shopify:block:select', e => {
    getDeferredMedia(e.target).forEach(hydrateDeferredMedia);
  });

  document.addEventListener('focusin', e => {
    const container = e.target.closest?.(DEFERRED_MEDIA_SELECTOR);
    if (container) hydrateDeferredMedia(container);
  });

  window.addEventListener('beforeprint', () => {
    getDeferredMedia(document).forEach(hydrateDeferredMedia);
  });
})();
